import React, { useMemo, useState } from 'react';
import CircularGallery from './CircularGallery';
import { useLang } from '../context/LanguageContext';

function getSafeImageUrl(url) {
  if (!url || String(url).includes('via.placeholder.com')) {
    return '';
  }

  return url;
}

function SectionEmptyState({ text }) {
  return (
    <div className="py-12 text-center">
      <p className="text-lg text-gray-400">{text}</p>
    </div>
  );
}

function getSafeExternalLink(url) {
  if (!url) {
    return '';
  }

  const trimmed = String(url).trim();
  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
}

export default function RecentWork({ works }) {
  const { t } = useLang();
  const [activeTab, setActiveTab] = useState('employment');
  const [selectedWork, setSelectedWork] = useState(null);

  const groupedWorks = useMemo(() => {
    const items = Array.isArray(works) ? works : [];
    return {
      employment: items.filter((work) => (work.section || 'employment') === 'employment'),
      product: items.filter((work) => (work.section || 'employment') === 'product'),
      skills: items.filter((work) => (work.section || 'employment') === 'skills'),
    };
  }, [works]);

  const activeWorks = groupedWorks[activeTab] || [];
  const skillsGalleryItems = groupedWorks.skills
    .map((work) => ({
      image: getSafeImageUrl(work.image),
      text: work.title,
    }))
    .filter((item) => item.image);

  if (!works || works.length === 0) return null;

  const handleProductCardClick = (work) => {
    const externalLink = getSafeExternalLink(work.link);

    if (externalLink) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
      return;
    }

    setSelectedWork(work);
  };

  return (
    <>
      <section className="bg-dark py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="mb-12 text-4xl font-bold text-white">{t.recentWorkTitle}</h2>

          <div className="-mx-6 mb-12 overflow-x-auto px-6 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max gap-3 whitespace-nowrap">
            <button
              onClick={() => setActiveTab('employment')}
              className={`shrink-0 rounded-full px-5 py-3 font-semibold transition ${
                activeTab === 'employment'
                  ? 'border border-gray-600 bg-dark-secondary text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {t.allWork}
            </button>
            <button
              onClick={() => setActiveTab('product')}
              className={`shrink-0 rounded-full px-5 py-3 font-semibold transition ${
                activeTab === 'product'
                  ? 'border border-gray-600 bg-dark-secondary text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {t.featured}
            </button>
            <button
              onClick={() => setActiveTab('skills')}
              className={`shrink-0 rounded-full px-5 py-3 font-semibold transition ${
                activeTab === 'skills'
                  ? 'border border-gray-600 bg-dark-secondary text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {t.skills}
            </button>
            </div>
          </div>

          {activeTab === 'employment' && (
            activeWorks.length === 0 ? (
              <SectionEmptyState text="这里还没有任职经历内容。" />
            ) : (
              <div className="space-y-6">
                {activeWorks.map((work) => (
                  (() => {
                    const imageUrl = getSafeImageUrl(work.image);
                    return (
                  <div
                    key={work.id}
                    onClick={() => setSelectedWork(work)}
                    className="group flex cursor-pointer items-start gap-6 border-b border-gray-700 py-6 transition hover:opacity-80 last:border-b-0"
                  >
                    <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-gray-700">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={work.title}
                          className="h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span className="text-xs text-gray-400">No Image</span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="text-lg font-semibold text-white transition group-hover:text-blue-400">
                          {work.title}
                        </h3>
                        {work.category && <span className="text-sm text-gray-500">{work.category}</span>}
                      </div>
                      <p className="mt-1 text-sm text-gray-400">{work.period}</p>
                      <p className="mt-2 line-clamp-2 text-sm text-gray-500">{work.description}</p>
                    </div>
                  </div>
                    );
                  })()
                ))}
              </div>
            )
          )}

          {activeTab === 'product' && (
            activeWorks.length === 0 ? (
              <SectionEmptyState text="这里还没有产品经历内容。" />
            ) : (
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                {activeWorks.map((work) => (
                  (() => {
                    const imageUrl = getSafeImageUrl(work.image);
                    const externalLink = getSafeExternalLink(work.link);
                    return (
                  <div
                    key={work.id}
                    className="group cursor-pointer overflow-hidden rounded-lg border border-gray-700 bg-dark-secondary transition hover:border-gray-600"
                    onClick={() => handleProductCardClick(work)}
                  >
                    <div className="flex h-48 items-center justify-center overflow-hidden bg-gray-700 transition group-hover:opacity-90">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={work.title}
                          className="h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.style.display = 'none';
                          }}
                        />
                      ) : (
                        <span className="text-sm text-gray-400">等待上传作品图片</span>
                      )}
                    </div>

                    <div className="p-6">
                      <div className="mb-3 flex flex-wrap items-center gap-3">
                        <h3 className="text-xl font-bold text-white">{work.title}</h3>
                        {work.category && <span className="text-sm text-gray-500">{work.category}</span>}
                      </div>

                      <p className="mb-4 line-clamp-2 text-sm text-gray-400">{work.description}</p>
                      {work.period && <p className="mb-4 text-xs text-gray-500">{work.period}</p>}

                      {work.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {work.tags.map((tag) => (
                            <span
                              key={`${work.id}-${tag}`}
                              className="rounded-full border border-blue-500/30 bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      {externalLink && (
                        <a
                          href={externalLink}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => event.stopPropagation()}
                          className="mt-5 inline-flex items-center text-sm font-semibold text-blue-400 transition hover:text-blue-300"
                        >
                          查看链接
                        </a>
                      )}
                    </div>
                  </div>
                    );
                  })()
                ))}
              </div>
            )
          )}

          {activeTab === 'skills' && (
            activeWorks.length === 0 ? (
              <SectionEmptyState text="这里还没有兴趣特长内容。" />
            ) : (
              <div>
                {skillsGalleryItems.length > 0 && (
                  <div className="relative h-[560px] overflow-hidden">
                    <CircularGallery
                      items={skillsGalleryItems}
                      bend={2}
                      textColor="#ffffff"
                      borderRadius={0.06}
                      scrollSpeed={3.4}
                      scrollEase={0.07}
                    />
                  </div>
                )}
              </div>
            )
          )}
        </div>
      </section>

      {selectedWork && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
          onClick={() => setSelectedWork(null)}
        >
          <div
            className="max-h-96 w-full max-w-2xl overflow-y-auto rounded-lg border border-gray-700 bg-dark-secondary"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-4 border-b border-gray-700 p-6">
              <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-gray-700">
                {getSafeImageUrl(selectedWork.image) ? (
                  <img
                    src={getSafeImageUrl(selectedWork.image)}
                    alt={selectedWork.title}
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <span className="text-xs text-gray-400">No Image</span>
                )}
              </div>
              <div className="flex-1">
                <h2 className="mb-1 text-2xl font-bold text-white">{selectedWork.title}</h2>
                {selectedWork.category && <p className="text-sm text-gray-500">{selectedWork.category}</p>}
                {selectedWork.period && <p className="text-gray-400">{selectedWork.period}</p>}
              </div>
              <button
                onClick={() => setSelectedWork(null)}
                className="text-2xl text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <p className="mb-6 whitespace-pre-line leading-relaxed text-gray-300">
                {selectedWork.description}
              </p>

              {getSafeExternalLink(selectedWork.link) && (
                <a
                  href={getSafeExternalLink(selectedWork.link)}
                  target="_blank"
                  rel="noreferrer"
                  className="mb-6 inline-flex items-center rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-sm font-semibold text-blue-300 transition hover:border-blue-400/50 hover:text-blue-200"
                >
                  打开项目链接
                </a>
              )}

              {selectedWork.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedWork.tags.map((tag) => (
                    <span
                      key={`${selectedWork.id}-${tag}`}
                      className="rounded-full border border-blue-500/30 bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
